import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Divider } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { RemoveOutlined } from '@material-ui/icons';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import { Button } from '../../../../shared/components/Button';
import { FormGroup } from '../../../../shared/components/Form/FormGroup';
import { Input } from '../../../../shared/components/Form/Input';
import { InputCurrency } from '../../../../shared/components/Form/Input/InputCurrency';
import { InputDatePicker } from '../../../../shared/components/Form/Input/InputDatePicker';
import { InputSlider } from '../../../../shared/components/Form/Input/InputSlider';
import { Select } from '../../../../shared/components/Form/Select';
import { Modal } from '../../../../shared/components/Modal';
import { useSale } from '../../../../shared/contexts/SaleDataContext';
import { useSnackMessages } from '../../../../shared/hooks/useSnackMessages';
import { api } from '../../../../shared/services/apiClient';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import { useStyles } from './styles';
import { FormValues } from './types';

type DescriptionItems = {
  id: number;
  value?: string;
  description?: string;
  budgetId?: number;
};

const schema = yup.object().shape({
  amount: yup
    .string()
    .required('O valor é obrigatório.')
    .max(254, 'No máximo 40 digitos.'),
  amount_paid: yup.string().nullable(),
  name: yup.string().nullable(),
  age: yup
    .number()
    .nullable()
    .notRequired()
    .typeError('A idade deve ser um número')
    .min(1, 'Você deve ter pelo menos 1 anos')
    .max(130, 'A idade deve ser menor ou igual a 130 anos'),
  party_theme: yup.string().nullable(),
  party_address: yup.string().nullable(),
  party_date: yup.string().required('A data é obrigatória.'),
  additional_information: yup.string().nullable(),
  client: yup
    .object({
      id: yup.string(),
      name: yup.string(),
    })
    .required('O cliente é obrigatório.'),
});

type IClient = {
  id: number;
  name: string;
};

interface ModalCreateScheduleProps {
  onClose: () => void;
  onCloseSuccess: () => void;
  isOpen: boolean;
}

export function ModalCreateSchedule({
  onClose,
  onCloseSuccess,
  isOpen,
}: ModalCreateScheduleProps) {
  const classes = useStyles();
  const { msgSuccess, msgError } = useSnackMessages();
  const history = useHistory();
  const { tempSaleData, setTempSaleData } = useSale();
  const [clients, setClients] = useState<IClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState('');
  const [descriptionItems, setDescriptionItems] = useState<DescriptionItems[]>(
    () => {
      return [
        {
          id: new Date().getTime(),
        },
      ];
    }
  );

  const handleNewDescriptionItem = useCallback(async () => {
    await setDescriptionItems((state) => [
      ...state,
      { id: new Date().getTime() + Math.random() },
    ]);
  }, []);

  const handleRemoveDescriptionItem = async (removeItem: DescriptionItems) => {
    const newDescriptionItems = await descriptionItems.filter(
      (item) => item.id !== removeItem.id
    );
    await setDescriptionItems(newDescriptionItems);
    let sumValue = 0;
    // eslint-disable-next-line array-callback-return
    newDescriptionItems.map((item) => {
      if (item.value) {
        sumValue += Number(item.value?.split(' ')[1].replace(',', '.'));
      }
    });

    setTotal(formatPrice(sumValue));
  };

  const handleChangeValue = (id: number, value?: string) => {
    const oldTotal = descriptionItems.reduce((accumulator, item) => {
      const currentValue =
        item?.value?.replace('R$', '').replace('.', '').replace(',', '.') ||
        '0';

      if (item.id !== id) {
        return accumulator + Number(currentValue);
      }
      return accumulator;
    }, 0);

    const currentValue = value?.replace('R$', '').replace('.', '').replace(',', '.') || '0';
    setTotal(formatPrice(oldTotal + Number(currentValue)));

    setDescriptionItems((state) =>
      state.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            value: formatPrice(Number(currentValue)),
          };
        }
        return item;
      })
    );
  };

  const [values, setValues] = useState([
    {
      id: new Date().getTime(),
      amount_paid: undefined,
      date_amount_paid: moment().format(),
    },
  ]);

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      amount: tempSaleData ? tempSaleData.value : undefined,
      name: '',
      age: undefined,
      party_theme: '',
      party_address: tempSaleData ? tempSaleData.client_event_address : '',
      party_date: tempSaleData
        ? tempSaleData.client_event_date
        : moment().format(),
      additional_information: tempSaleData
        ? tempSaleData.client_event_place
        : '',
      client: tempSaleData ? tempSaleData.client : undefined,
    },
  });

  const onSubmit = useCallback(
    async ({
      amount,
      client,
      party_date,
      additional_information,
      age,
      amount_paid,
      name,
      party_address,
      party_theme,
      values,
    }: FormValues) => {
      const formatItems = descriptionItems?.map((item, index) => {
        const description = getValues(`items[${index}][description]`);

        const value = String(item?.value);

        const currentValue = value?.replace('R$', '').replace('.', '').replace(',', '.') || '0';

        return {
          ...item,
          description,
          value:
            currentValue !== 'undefined' && currentValue !== undefined
              ? `R$ ${formatPrice(Number(currentValue))}`
              : undefined,
        };
      });

      const errorValidateValues = values?.find(
        (value) => value?.amount_paid && !value?.date_amount_paid
      );
      if (errorValidateValues) {
        msgError('Lembre-se de informa a data para cada valor informado.');
        return;
      }

      try {
        const formatData = {
          amount: Number(
            amount
              .toString()
              .replace('R$', '')
              .replace(/\./g, '')
              .replace(',', '.')
          ),
          items: formatItems,
          client_id: client.id,
          party_date: moment(party_date).format('YYYY-MM-DDTHH:mm:ssZ'),
          additional_information,
          age,
          name,
          party_address,
          party_theme,
          amounts: values?.map((value) => {
            if (value?.amount_paid) {
              return {
                amount_paid: Number(
                  value.amount_paid
                    .toString()
                    .replace('R$', '')
                    .replace(',', '.')
                ),
                date_amount_paid: moment(value.date_amount_paid).format(
                  'YYYY-MM-DDTHH:mm:ssZ'
                ),
              };
            }
            return undefined;
          }),
        };

        await api.post('/sales/schedule', formatData);

        msgSuccess('Agenda de vendas adicionada com sucesso!');
        setTempSaleData(null);

        reset();
        onCloseSuccess();
      } catch (err: any) {
        msgError(err?.response?.data?.message || err?.response?.data?.error);
      }
    },
    [msgError, msgSuccess, reset, onCloseSuccess]
  );

  const handleClose = () => {
    setTempSaleData(null);
    reset();
    onClose();
  };

  const handleNewValue = useCallback(() => {
    setValues((state) => [
      ...state,
      {
        id: new Date().getTime() + Math.random(),
        amount_paid: undefined,
        date_amount_paid: moment().format(),
      },
    ]);
  }, []);

  useEffect(() => {
    setLoading(true);

    api
      .get<{ results: IClient[] }>('/clients/list')
      .then((response) => {
        setClients(response.data.results);

        setValue('client', response.data?.results?.[0]);

        if (tempSaleData?.client) {
          const selectedClient = response.data.results.find(
            (cli) => cli.id === tempSaleData.client.id
          );

          setValue('client', selectedClient || null);
        } else {
          setValue('client', response.data.results[0]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setValue]);

  useEffect(() => {
    if (total) {
      setValue('amount', total);
    }
  }, [total, setValue]);

  useEffect(() => {
    if (tempSaleData?.items?.length) {
      setDescriptionItems(tempSaleData.items);

      let sum = 0;

      tempSaleData.items.forEach((item, index) => {
        setValue(`items[${index}][description]`, item.description);

        if (item.value) {
          setValue(`items[${index}][value]`, item.value);

          const valueFormatted = item.value
            .replace('R$', '')
            .replace('.', '')
            .replace(',', '.');

          sum += Number(valueFormatted);
        }
      });

      const formattedTotal = formatPrice(sum);
      setTotal(formattedTotal);
      setValue('amount', formattedTotal);
    }
  }, [tempSaleData?.items, setValue]);

  return (
    <Modal title="Adicionar Encomenda" open={isOpen} onClose={handleClose}>
      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" flexDirection="column" width="100%">
            <FormGroup title="Informações do cliente">
              {!!clients.length && (
                <Select
                  options={clients}
                  value={tempSaleData ? tempSaleData.client : ''}
                  name="client"
                  control={control}
                  error={errors}
                  variant="outlined"
                  label="Cliente"
                  fullWidth
                  required
                  disabled={isSubmitting}
                />
              )}
            </FormGroup>

            {!loading && !clients.length && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <Box>
                  Você ainda não cadastrou nenhum cliente. Clique no botão
                  abaixo!
                </Box>
                <Button onClick={() => history.push('/records/clients')}>
                  Clientes
                </Button>
              </Box>
            )}

            <FormGroup
              title="Adicione os ítens e valores inclusos na encomenda"
              mb={2}
            >
              <Box
                display="flex"
                flexDirection="column"
                gridGap={8}
                width="100%"
              >
                {descriptionItems.map((item, index) => {
                  return (
                    <Box
                      key={item.id}
                      display="grid"
                      gridTemplateColumns="2fr 1fr 40px"
                      width="100%"
                      gridGap={8}
                    >
                      <Input
                        label="Descrição do Item"
                        name={`items[${index}][description]`}
                        control={control}
                        error={errors}
                        variant="outlined"
                        multiline
                        fullWidth
                        required
                        inputProps={{ maxLength: 180 }}
                      />
                      <InputCurrency
                        label="Valor"
                        name={`items[${index}][value]`}
                        control={control}
                        error={errors}
                        variant="outlined"
                        fullWidth
                        className={classes.input}
                        style={{ marginBottom: 0 }}
                        onChange={(value) =>
                          handleChangeValue(item.id, value?.target.value)
                        }
                      />
                      <Box
                        display="inline-flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Button
                          color="primary"
                          size="small"
                          onClick={() => handleRemoveDescriptionItem(item)}
                          disabled={descriptionItems.length === 1}
                          style={{
                            minWidth: 'auto',
                            borderRadius: '50%',
                            height: 35,
                            width: 35,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <RemoveOutlined />
                        </Button>
                      </Box>
                    </Box>
                  );
                })}

                <Box display="flex" width="100%" mb={1}>
                  <Button
                    type="button"
                    className={classes.button}
                    styleMotion={{ width: '100%' }}
                    onClick={handleNewDescriptionItem}
                  >
                    Clique aqui para adicionar mais itens!
                  </Button>
                </Box>

                <Divider />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  fontWeight="bold"
                  fontSize="1.1rem"
                  color="primary.dark"
                  lineHeight="1.8"
                >
                  <Box fontSize="1rem">Valor Total:</Box>{' '}
                  <Box>R$ {total || '0,00'}</Box>
                </Box>
              </Box>
            </FormGroup>

            <FormGroup title="Pagamentos">
              <Box
                borderRadius={8}
                px={1}
                pb={0}
                width="100%"
                display="flex"
                flexDirection="column"
                bgcolor="#f9f9f9"
              >
                {values.map((value, index) => (
                  <Box
                    display="flex"
                    flexDirection="row"
                    key={value.id}
                    width="100%"
                  >
                    <InputCurrency
                      name={`values[${index}][amount_paid]`}
                      control={control}
                      error={errors}
                      variant="outlined"
                      label="Valor pago"
                      fullWidth
                      disabled={isSubmitting}
                      style={{ width: '100%', marginRight: 15 }}
                    />

                    <InputDatePicker
                      name={`values[${index}][date_amount_paid]`}
                      label="Data do Pagamento"
                      variant="outlined"
                      control={control}
                      error={errors}
                      disabled={isSubmitting}
                      style={{ width: '100%' }}
                    />
                  </Box>
                ))}
                <Box display="flex" width="100%" mb={1}>
                  <Button
                    type="button"
                    className={classes.button}
                    styleMotion={{ width: '100%' }}
                    onClick={handleNewValue}
                  >
                    Clique aqui para adicionar mais pagamentos!
                  </Button>
                </Box>
              </Box>
            </FormGroup>

            <FormGroup title="Informações do evento">
              <Input
                name="name"
                control={control}
                error={errors}
                variant="outlined"
                label="Nome do Evento"
                fullWidth
                disabled={isSubmitting}
              />
              <Input
                name="age"
                control={control}
                error={errors}
                variant="outlined"
                label="Idade"
                fullWidth
                disabled={isSubmitting}
                type="number"
              />
              <Input
                name="party_theme"
                control={control}
                error={errors}
                variant="outlined"
                label="Tema da festa"
                fullWidth
                disabled={isSubmitting}
              />
              <Input
                name="party_address"
                control={control}
                error={errors}
                variant="outlined"
                label="Endereço da festa"
                fullWidth
                disabled={isSubmitting}
              />
              <Input
                name="additional_information"
                control={control}
                error={errors}
                variant="outlined"
                label="Informações adicionais"
                fullWidth
                disabled={isSubmitting}
                inputProps={{ maxLength: 500 }}
                placeholder="500 caracteres"
              />

              <InputDatePicker
                name="party_date"
                label="Data da festa"
                variant="outlined"
                control={control}
                error={errors}
                disabled={isSubmitting}
                required
              />
            </FormGroup>
          </Box>

          <DialogActions style={{ padding: 0 }}>
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              gridGap={8}
              mt={4}
              alignItems="stretch"
            >
              <Button
                type="submit"
                size="large"
                color="primary"
                variant="contained"
                disabled={isSubmitting || !clients.length}
                style={{ width: '100%' }}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                onClick={handleClose}
                color="primary"
                variant="outlined"
                size="large"
                style={{ width: '100%' }}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </Box>
          </DialogActions>
        </Box>
      </DialogContent>
    </Modal>
  );
}
